/**
 * The products a `cart_contains_product` / `cart_not_contains_product` condition targets.
 *
 * 🚨 Reads the legacy single-product fields when `products` is empty. Conditions saved before the
 * list existed carry only `product_id` / `product_name`, and nothing rewrites them — the merchant
 * may never open that rate again. Both renderers and the backend go through here, so the fallback
 * lives in one place rather than three.
 */
export function conditionProducts(c) {
    if (c.products?.length)
        return c.products;
    return c.product_id != null ? [{ id: c.product_id, name: c.product_name ?? '' }] : [];
}
/** Evaluate a single condition against the cart facts. */
function conditionMet(c, ctx) {
    switch (c.type) {
        case 'order_total_gte':
            return ctx.orderTotal >= c.value;
        case 'order_total_lt':
            return ctx.orderTotal < c.value;
        case 'quantity_gte':
            return ctx.quantity >= c.value;
        case 'quantity_lt':
            return ctx.quantity < c.value;
        // ANY-OF, both ways round: "contains" needs one match, "does not contain" needs none.
        // 🚨 A condition with NO product selected stays false, exactly as the single-product version
        // did — an unconfigured condition must not silently start passing (for `not_contains` that
        // would offer the rate to every cart, which is the merchant's rule inverted).
        case 'cart_contains_product': {
            const ids = conditionProducts(c);
            return ids.length > 0 && ids.some((p) => ctx.productIds.includes(p.id));
        }
        case 'cart_not_contains_product': {
            const ids = conditionProducts(c);
            return ids.length > 0 && !ids.some((p) => ctx.productIds.includes(p.id));
        }
        default:
            return true;
    }
}
/**
 * Whether a rate should be offered for the current cart: every condition is met. A rate with no
 * conditions is always eligible.
 *
 * 🚨 COUNTRY GATING — DISABLED (2026-09-08), commented out below rather than deleted. The admin
 * control that set `country_only` / `countries` is disabled too (`admin/src/pages/ShippingRates.tsx`
 * → `RateCountryGate`, which carries the restore checklist). **The two halves must move together.**
 * Leaving this branch live with no UI would let a config saved earlier with `country_only: true`
 * keep hiding rates from every buyer, with nothing on screen to explain it or clear it — and the
 * gate fails CLOSED when the buyer country cannot be resolved, so the failure is total, not partial.
 *
 * The schema still carries both fields and `getPublicConfig` still mirrors `store_country`, so a
 * stored selection survives the round trip and returns intact if this is switched back on.
 */
export function rateEligible(rate, ctx) {
    // if (rate.country_only) {
    //   const country = ctx.country?.toUpperCase();
    //   if (!country || !rate.countries.map((c) => c.toUpperCase()).includes(country)) return false;
    // }
    return rate.conditions.every((c) => conditionMet(c, ctx));
}
/** The eligible rates for a cart, in config order. */
export function eligibleRates(rates, ctx) {
    return rates.filter((r) => rateEligible(r, ctx));
}
/**
 * How much of the SELECTED rate's cost a free-shipping coupon waives.
 *
 * 🚨 Shared for the same reason `rateEligible` above it is: the storefront quotes this number in
 * the totals box, the admin preview draws the same box, and the backend charges it on the order.
 * A copy that rounded or capped differently would quote the buyer one shipping cost and bill
 * another — the one divergence a COD form cannot afford, because the buyer pays a human at
 * the door from the figure the form showed them.
 *
 * `only_cheapest_shipping` is documented as "only applies to the cheapest eligible shipping
 * option" and nothing more, which leaves two readings: the pricier rate gets NOTHING, or it gets
 * the cheapest rate's worth off and the buyer pays the upgrade. We take the second — it is the
 * ordinary commerce meaning of a capped shipping discount, and it is the reading that cannot
 * overcharge relative to the merchant's intent ("I'll cover the cheap option"). Revisit against a
 * live coupon if a merchant reports otherwise; `couponProbe.ts` prints the field.
 *
 * Returns the amount to SUBTRACT from the selected rate, never more than that rate costs.
 */
export function waivedShipping(selected, eligible, coupon) {
    if (!coupon?.freeShipping || !selected)
        return 0;
    const cost = selected.price;
    if (!(cost > 0))
        return 0;
    if (!coupon.onlyCheapest)
        return cost;
    // Seeded with the selected rate's own cost so an empty/stale eligible list degrades to "waive
    // what was picked" rather than to zero — the buyer keeps the discount the form already showed.
    const cheapest = eligible.reduce((min, r) => (r.price < min ? r.price : min), cost);
    return Math.min(Math.max(0, cheapest), cost);
}
//# sourceMappingURL=shipping.logic.js.map